import { useState } from "react";
import { useTranslation } from "react-i18next";

const infoIcons = ["ri-map-pin-2-fill", "ri-phone-fill", "ri-mail-fill", "ri-time-fill"];
const infoTitleKeys = ["contact.info1.title", "contact.info2.title", "contact.info3.title", "contact.info4.title"];
const infoTextKeys = ["contact.info1.text", "contact.info2.text", "contact.info3.text", "contact.info4.text"];

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [selectedPlan, setSelectedPlan] = useState("opt2");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d76hr0tr5g02gnd6a130", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) { setStatus("success"); form.reset(); }
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-[#0b0b0b] py-24 px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#c9a84c]/4 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-[4px] uppercase">{t("contact.badge")}</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t("contact.title1")} <span className="text-[#c9a84c]">{t("contact.title2")}</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base">{t("contact.desc")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="space-y-6 mb-10">
              {infoIcons.map((icon, i) => (
                <div key={icon} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a84c]/10 rounded-xl shrink-0">
                    <i className={`${icon} text-[#c9a84c]`} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-0.5">{t(infoTitleKeys[i])}</p>
                    <p className="text-white/80 text-sm">{t(infoTextKeys[i])}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/852ba54a4e8db9557d7feee002fdba16.jpg"
                alt="Saudi Aramco Headquarters"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-5">
                <p className="text-[#c9a84c] font-bold text-sm mb-1">{t("contact.hqLabel")}</p>
                <p className="text-white/50 text-xs">{t("contact.hqSub")}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#c9a84c]/20 rounded-full mb-4">
                  <i className="ri-checkbox-circle-fill text-[#c9a84c] text-3xl" />
                </div>
                <h3 className="text-white font-black text-xl mb-2">{t("contact.successTitle")}</h3>
                <p className="text-white/60 text-sm max-w-xs leading-relaxed">{t("contact.successDesc")}</p>
                <button onClick={() => setStatus("idle")} className="mt-6 text-[#c9a84c] text-sm font-semibold cursor-pointer hover:underline">
                  {t("contact.anotherRequest")}
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-white font-black text-lg mb-6">{t("contact.formTitle")}</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-2 block">{t("contact.label.name")}</label>
                    <input name="full_name" type="text" required placeholder={t("contact.ph.name")}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#c9a84c]/50 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors placeholder:text-white/20" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-2 block">{t("contact.label.email")}</label>
                    <input name="email" type="email" required placeholder={t("contact.ph.email")}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#c9a84c]/50 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors placeholder:text-white/20" />
                  </div>
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-2 block">{t("contact.label.phone")}</label>
                  <input name="phone" type="tel" placeholder={t("contact.ph.phone")}
                    className="w-full bg-white/5 border border-white/10 focus:border-[#c9a84c]/50 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors placeholder:text-white/20" />
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-2 block">{t("contact.label.contract")}</label>
                  <select name="contract_plan" value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-full bg-[#1a1500] border border-white/10 focus:border-[#c9a84c]/50 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors cursor-pointer">
                    <option value="opt1">{t("contact.opt1")}</option>
                    <option value="opt2">{t("contact.opt2")}</option>
                    <option value="opt3">{t("contact.opt3")}</option>
                  </select>
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-2 block">{t("contact.label.message")}</label>
                  <textarea name="message" rows={3} maxLength={500} placeholder={t("contact.ph.message")}
                    className="w-full bg-white/5 border border-white/10 focus:border-[#c9a84c]/50 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors placeholder:text-white/20 resize-none" />
                </div>

                {status === "error" && <p className="text-red-400 text-xs">{t("contact.error")}</p>}

                <button type="submit" disabled={status === "sending"}
                  className="w-full bg-[#c9a84c] hover:bg-[#b8962e] disabled:opacity-60 text-[#0b0b0b] font-black text-sm py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                  {status === "sending" ? t("contact.submitting") : t("contact.submit")}
                </button>

                <p className="text-white/30 text-xs text-center leading-relaxed">{t("contact.disclaimer")}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
